import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  HourglassEmpty as ProcessingIcon,
  Cancel as RejectedIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getAllPrivacyRequests,
  updatePrivacyRequestStatus,
  PrivacyRequest,
} from '../../services/privacyRequestService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';

export const AdminPrivacyRequests: React.FC = () => {
  const { theme } = useTheme();
  const [requests, setRequests] = useState<PrivacyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<PrivacyRequest | null>(null);
  const [updating, setUpdating] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [error, setError] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [newStatus, setNewStatus] = useState<'pending' | 'processing' | 'completed' | 'rejected'>('processing');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await getAllPrivacyRequests();
      setRequests(data);
    } catch (err) {
      console.error('Erro ao carregar solicitações:', err);
      setError('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedRequest?.id) return;

    try {
      setUpdating(true);
      await updatePrivacyRequestStatus(selectedRequest.id, newStatus, adminNotes.trim() || undefined);
      await loadRequests();
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      setError('Erro ao atualizar status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedRequest?.userId || selectedRequest.userId === 'unauthenticated') {
      setError('Usuário não identificado ou não autenticado.');
      return;
    }

    try {
      setDeletingUser(true);
      const adminDeleteUser = httpsCallable(functions, 'adminDeleteUser');
      await adminDeleteUser({ userId: selectedRequest.userId });
      
      // Atualizar status para completed
      if (selectedRequest.id) {
        await updatePrivacyRequestStatus(
          selectedRequest.id, 
          'completed', 
          (adminNotes ? adminNotes + '\n' : '') + 'Conta deletada via Admin Panel.'
        );
      }
      
      await loadRequests();
      setConfirmDeleteOpen(false);
      setSelectedRequest(null);
      setAdminNotes('');
      // alert('Usuário deletado com sucesso.');
    } catch (err: any) {
      console.error('Erro ao deletar usuário:', err);
      setError('Erro ao deletar usuário: ' + (err.message || 'Erro desconhecido'));
      setConfirmDeleteOpen(false);
    } finally {
      setDeletingUser(false);
    }
  };

  const pendingRequests = requests.filter((r) => r.status === 'pending');
  const processingRequests = requests.filter((r) => r.status === 'processing');
  const completedRequests = requests.filter((r) => r.status === 'completed');
  const rejectedRequests = requests.filter((r) => r.status === 'rejected');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'processing':
        return 'Em Processamento';
      case 'completed':
        return 'Concluída';
      case 'rejected':
        return 'Rejeitada';
      default:
        return status;
    }
  };

  const getRequestTypeLabel = (type: string) => {
    return type === 'delete_account' ? 'Exclusão de Conta' : 'Exportação de Dados';
  };

  const getRequestTypeIcon = (type: string) => {
    return type === 'delete_account' ? <DeleteIcon /> : <DownloadIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Carregando" sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  const renderRequestCard = (request: PrivacyRequest) => (
    <Card
      key={request.id}
      component="button"
      type="button"
      aria-label={`Abrir solicitação de ${getRequestTypeLabel(request.requestType)} de ${request.userName || request.userEmail}`}
      sx={{
        p: 2,
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        display: 'block',
        backgroundColor: theme.colors.card,
        border: `1px solid ${theme.colors.border}`,
        cursor: 'pointer',
        '&:hover': {
          borderColor: theme.colors.primary,
        },
      }}
      onClick={() => {
        setSelectedRequest(request);
        setAdminNotes(request.adminNotes || '');
        setNewStatus(request.status);
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {getRequestTypeIcon(request.requestType)}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
              {getRequestTypeLabel(request.requestType)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Usuário: {request.userName} ({request.userEmail})
          </Typography>
        </Box>
        <Chip
          label={getStatusLabel(request.status)}
          color={getStatusColor(request.status) as any}
          size="small"
        />
      </Box>
      {request.reason && (
        <Typography
          variant="body2"
          sx={{
            color: theme.colors.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mt: 1,
          }}
        >
          {request.reason}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {request.createdAt?.toLocaleDateString('pt-BR')} às{' '}
        {request.createdAt?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </Typography>
    </Card>
  );

  return (
    <Box sx={{ px: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<PendingIcon />}
          label={`${pendingRequests.length} Pendentes`}
          color="warning"
          variant="outlined"
        />
        <Chip
          icon={<ProcessingIcon />}
          label={`${processingRequests.length} Em Processamento`}
          color="info"
          variant="outlined"
        />
        <Chip
          icon={<CompletedIcon />}
          label={`${completedRequests.length} Concluídas`}
          color="success"
          variant="outlined"
        />
        <Chip
          icon={<RejectedIcon />}
          label={`${rejectedRequests.length} Rejeitadas`}
          color="error"
          variant="outlined"
        />
      </Box>

      {/* Solicitações Pendentes */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
        Solicitações Pendentes
      </Typography>

      {pendingRequests.length === 0 ? (
        <Card sx={{ p: 3, mb: 4, textAlign: 'center', backgroundColor: theme.colors.card }}>
          <Typography color="text.secondary">Nenhuma solicitação pendente</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {pendingRequests.map(renderRequestCard)}
        </Box>
      )}

      {/* Solicitações Em Processamento */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
        Em Processamento
      </Typography>

      {processingRequests.length === 0 ? (
        <Card sx={{ p: 3, mb: 4, textAlign: 'center', backgroundColor: theme.colors.card }}>
          <Typography color="text.secondary">Nenhuma solicitação em processamento</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {processingRequests.map(renderRequestCard)}
        </Box>
      )}

      {/* Solicitações Concluídas */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
        Concluídas
      </Typography>

      {completedRequests.length === 0 ? (
        <Card sx={{ p: 3, mb: 4, textAlign: 'center', backgroundColor: theme.colors.card }}>
          <Typography color="text.secondary">Nenhuma solicitação concluída</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {completedRequests.map(renderRequestCard)}
        </Box>
      )}

      {/* Solicitações Rejeitadas */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
        Rejeitadas
      </Typography>

      {rejectedRequests.length === 0 ? (
        <Card sx={{ p: 3, mb: 4, textAlign: 'center', backgroundColor: theme.colors.card }}>
          <Typography color="text.secondary">Nenhuma solicitação rejeitada</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rejectedRequests.map(renderRequestCard)}
        </Box>
      )}

      {/* Dialog de Detalhes */}
      <Dialog
        aria-labelledby="adminprivacyrequests-dlg1-title"
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }}
      >
        {selectedRequest && (
          <>
            <DialogTitle id="adminprivacyrequests-dlg1-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Solicitação de Privacidade
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedRequest(null)} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Tipo:</strong> {getRequestTypeLabel(selectedRequest.requestType)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Usuário:</strong> {selectedRequest.userName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Email:</strong> {selectedRequest.userEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Data:</strong> {selectedRequest.createdAt?.toLocaleDateString('pt-BR')} às{' '}
                  {selectedRequest.createdAt?.toLocaleTimeString('pt-BR')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Status:</strong>{' '}
                  <Chip
                    label={getStatusLabel(selectedRequest.status)}
                    color={getStatusColor(selectedRequest.status) as any}
                    size="small"
                  />
                </Typography>

                {selectedRequest.reason && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Motivo:
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.colors.card,
                        borderRadius: theme.radii.medium,
                        border: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedRequest.reason}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedRequest.adminNotes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Notas do Administrador:
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.colors.card,
                        borderRadius: theme.radii.medium,
                        border: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedRequest.adminNotes}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Novo Status</InputLabel>
                  <Select
                    value={newStatus}
                    label="Novo Status"
                    onChange={(e) => setNewStatus(e.target.value as any)}
                  >
                    <MenuItem value="pending">Pendente</MenuItem>
                    <MenuItem value="processing">Em Processamento</MenuItem>
                    <MenuItem value="completed">Concluída</MenuItem>
                    <MenuItem value="rejected">Rejeitada</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Notas do Administrador"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Adicione notas sobre o processamento desta solicitação..."
                />

                {selectedRequest.requestType === 'delete_account' && 
                 selectedRequest.status !== 'completed' && 
                 selectedRequest.userId !== 'unauthenticated' && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.colors.border}` }}>
                     <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setConfirmDeleteOpen(true)}
                      disabled={updating || deletingUser}
                    >
                      Deletar Conta do Usuário
                    </Button>
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                      Cuidado: Esta ação é irreversível e deletará todos os dados.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button onClick={() => setSelectedRequest(null)} sx={{ color: theme.colors.text.secondary }}>
                Cancelar
              </Button>
              <Button
                onClick={handleStatusChange}
                variant="contained"
                disabled={updating || deletingUser}
                sx={{
                  backgroundColor: theme.colors.primary,
                  '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 },
                }}
              >
                {updating ? <CircularProgress aria-label="Carregando" size={20} /> : 'Atualizar Status'}
              </Button>
            </DialogActions>

            {/* Dialog de Confirmação de Exclusão */}
            <Dialog
              aria-labelledby="adminprivacyrequests-dlg2-title"
              open={confirmDeleteOpen}
              onClose={() => setConfirmDeleteOpen(false)}
            >
              <DialogTitle id="adminprivacyrequests-dlg2-title" sx={{ color: 'error.main', fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
              <DialogContent>
                <Typography>
                  Tem certeza que deseja deletar a conta de <strong>{selectedRequest.userName}</strong>?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Isso removerá permanentemente o usuário do Auth e todos os seus dados do Firestore.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
                <Button 
                  onClick={handleDeleteUser} 
                  variant="contained" 
                  color="error"
                  disabled={deletingUser}
                >
                  {deletingUser ? <CircularProgress aria-label="Carregando" size={20} color="inherit" /> : 'Confirmar e Deletar'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Dialog>
    </Box>
  );
};
